/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useMemo, useRef, useCallback, useEffect, useState, lazy, Suspense, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRevalidateFilledFields } from '@/hooks/use-revalidate-filled-fields';
import { useFetcher, useResolvedPath, useRevalidator } from 'react-router';
import { ToggleCard, ToggleCardEdit, ToggleCardSummary } from '@/components/toggle-card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Link } from '@/components/link';
import { FormFloatingLabelInput } from '@/components/form-fields';
import { Typography } from '@/components/typography';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { FloatingLabelInput } from '@/components/ui/floating-label-input';
import { useBasket } from '@/providers/basket';
import {
    createContactInfoSchema,
    NAME_PATTERN,
    PINCODE_PATTERN,
    INDIAN_MOBILE_PATTERN,
    validMessage,
    type ContactInfoData,
} from '@/lib/checkout/schemas';
import { useLoginSuggestion } from '@/hooks/use-customer-lookup';
import { useCustomerProfile } from '@/hooks/checkout/use-customer-profile';
import { getContactInfoFromCustomer } from '@/lib/customer/profile-utils';

import type { CheckoutActionData } from '../types';
import type { action as authorizePasswordlessEmailAction } from '@/routes/action.authorize-passwordless-email';
import { useTranslation } from 'react-i18next';
import { useCheckoutContext } from '@/hooks/use-checkout';
import { stripNonDigits, stripCountryCode } from '@/lib/address/phone-utils';
import type { OtpFlowActiveRef } from '@/hooks/use-checkout-actions';
import { Spinner } from '@/components/spinner';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { TurnstileWidget } from '@/components/security/turnstile-widget';
import { getTurnstileSiteKey, getTurnstileMode, isTurnstileEnabled } from '@/lib/turnstile/utils';
import { resourceRoutes } from '@/route-paths';
import { SiApple } from '@icons-pack/react-simple-icons';
import { Check } from 'lucide-react';
import { GoogleIcon } from '@/components/icons';
import facebookIcon from '/images/facebook.svg';
import { useMsg91Otp, maskMsg91Identifier } from '@/hooks/use-msg91-otp';
import { cn } from '@/lib/utils';

const OtpModal = lazy(() => import('@/components/login/otp-modal'));
const SignInFlow = lazy(() => import('@/components/header/user-actions/sign-in-flow'));
const Msg91OtpPopup = lazy(() => import('@/components/login/msg91-otp-popup'));

interface ContactInfoProps {
    onSubmit: (data: ContactInfoData) => void;
    isLoading: boolean;
    actionData?: CheckoutActionData;
    onRegisteredUserChoseGuest?: (isGuest: boolean) => void;
    /** Called when shopper completes passwordless OTP at contact (sign-in). Resets UI that was applied for "checkout as guest" skip. */
    onPasswordlessOtpVerified?: () => void;
    /** When true, hide login hints in summary (used after "Checkout as guest" on passwordless OTP — treat as plain guest UX). */
    suppressRegisteredEmailLoginHints?: boolean;
    /** When set, kept in sync so checkout does not advance from contact while OTP modal is open or authorize in flight. */
    otpFlowActiveRef?: OtpFlowActiveRef;
    /** Initial OTP sending state — used in Storybook to show the spinner in the email field without triggering fetcher logic */
    defaultOtpSending?: boolean;
    // Step state managed by container
    isCompleted: boolean;
    isEditing: boolean;
    onEdit: () => void;
}

export default function ContactInfo({
    onSubmit,
    isLoading,
    actionData: _actionData,
    onRegisteredUserChoseGuest,
    onPasswordlessOtpVerified,
    suppressRegisteredEmailLoginHints = false,
    otpFlowActiveRef,
    defaultOtpSending = false,
    isCompleted,
    isEditing,
    onEdit: _onEdit,
}: ContactInfoProps) {
    const cart = useBasket();

    const loginSuggestion = useLoginSuggestion();
    const customerProfile = useCustomerProfile();
    const { exitEditMode, shipmentDistribution } = useCheckoutContext();
    const { t } = useTranslation('checkout');
    const appConfig = useConfig();

    const customerContactInfo = getContactInfoFromCustomer(customerProfile);

    const schema = useMemo(() => createContactInfoSchema(t), [t]);
    const authorizePasswordlessEmailPath = useResolvedPath(resourceRoutes.authorizePasswordlessEmail).pathname;
    const revalidator = useRevalidator();
    const passwordlessEmailFetcher = useFetcher<typeof authorizePasswordlessEmailAction>({
        key: 'contact-authorize-passwordless-email',
    });
    const lastEmailSentRef = useRef<string | null>(null);
    const otpSuccessRevalidatingRef = useRef(false);
    const [isOtpOpen, setIsOtpOpen] = useState(false);
    const [otpModalEmail, setOtpModalEmail] = useState('');
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    // Form mode: phone (default) or email
    const [contactMode, setContactMode] = useState<'phone' | 'email'>(() => {
        if (cart?.customerInfo?.email && !cart?.customerInfo?.phone) {
            return 'email';
        }
        return 'phone';
    });
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    // Separate from the Request OTP checkbox so the details form never shows up pre-ticked
    const [agreedToTermsDetails, setAgreedToTermsDetails] = useState(false);

    // MSG91 & Verification state
    const [msg91Error, setMsg91Error] = useState<string | null>(null);
    const [isCheckingUser, setIsCheckingUser] = useState(false);
    const [showMockOtp, setShowMockOtp] = useState(false);
    const [mockOtpCode, setMockOtpCode] = useState('');
    const [verifyingMockOtp, setVerifyingMockOtp] = useState(false);
    const [pendingIdentifier, setPendingIdentifier] = useState('');
    const [pendingResolvedEmail, setPendingResolvedEmail] = useState<string | null>(null);
    const [isPendingExistingUser, setIsPendingExistingUser] = useState(false);

    // Post-OTP guest details form (name / mobile / email / pincode)
    const [showDetailsForm, setShowDetailsForm] = useState(false);
    const [detailsFirstName, setDetailsFirstName] = useState('');
    const [detailsLastName, setDetailsLastName] = useState('');
    const [detailsFullName, setDetailsFullName] = useState('');
    const [detailsPhone, setDetailsPhone] = useState('');
    const [detailsEmail, setDetailsEmail] = useState('');
    const [detailsPincode, setDetailsPincode] = useState('');
    const [phoneVerifiedInDetails, setPhoneVerifiedInDetails] = useState(false);
    const [emailVerifiedInDetails, setEmailVerifiedInDetails] = useState(false);
    const [verifyingInline, setVerifyingInline] = useState<'email' | 'phone' | null>(null);
    const [detailsError, setDetailsError] = useState<string | null>(null);
    // Fields the shopper has left at least once — their errors show inline from then on
    const [detailsTouched, setDetailsTouched] = useState<Record<string, boolean>>({});
    // Inline Verify errors (invalid email / mobile) shown under that field instead of the top banner
    const [detailsVerifyError, setDetailsVerifyError] = useState<{ field: 'email' | 'phone'; message: string } | null>(
        null
    );
    // Which identifier the shopper OTP-verified to reach the details form — that field is locked,
    // the other one gets an inline "Verify".
    const [signupVia, setSignupVia] = useState<'phone' | 'email'>('phone');
    // The MSG91 accessToken from the initial phone/email OTP — kept to exchange for a
    // SLAS session (via verify-msg91-otp with isSignup=true) after details are submitted.
    const [newUserAccessToken, setNewUserAccessToken] = useState<string | null>(null);

    // Custom MSG91 OTP popup state (replaces MSG91's default hosted popup UI)
    const [showMsg91OtpPopup, setShowMsg91OtpPopup] = useState(false);
    const [msg91MaskedIdentifier, setMsg91MaskedIdentifier] = useState('');
    const [msg91PopupError, setMsg91PopupError] = useState<string | null>(null);
    const [verifyingMsg91Otp, setVerifyingMsg91Otp] = useState(false);
    const { sendOtp: sendMsg91Otp, verifyOtp: verifyMsg91OtpCode, resendOtp: resendMsg91Otp } = useMsg91Otp();

    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const [turnstileBypassed, setTurnstileBypassed] = useState(false);
    const turnstileResetRef = useRef<(() => void) | null>(null);
    const turnstileExecuteRef = useRef<(() => void) | null>(null);
    const tokenConsumedRef = useRef(false);
    const [verificationError, setVerificationError] = useState<string | null>(null);
    const verificationFailureCountRef = useRef(0);
    const MAX_VERIFICATION_RETRIES = 3;

    const turnstileEnabled = isTurnstileEnabled(appConfig);
    const turnstileMode = getTurnstileMode(appConfig);
    const turnstileSiteKey = useMemo(() => {
        if (!turnstileEnabled) return null;
        if (typeof window !== 'undefined') {
            const baseUrl = `${window.location.protocol}//${window.location.host}`;
            return getTurnstileSiteKey(appConfig, baseUrl);
        }
        return null;
    }, [appConfig, turnstileEnabled]);

    const [showTurnstile, setShowTurnstile] = useState(false);
    const turnstilePending = !!(turnstileEnabled && turnstileSiteKey && !turnstileToken && !turnstileBypassed);

    const resetTurnstile = useCallback(() => {
        setTurnstileToken(null);
        turnstileResetRef.current?.();
    }, []);

    const handleTurnstileSuccess = useCallback((token: string) => {
        tokenConsumedRef.current = false;
        setTurnstileToken(token);
    }, []);

    const handleTurnstileError = useCallback(() => {
        setTurnstileToken(null);
    }, []);

    const handleTurnstileExpire = useCallback(() => {
        setTurnstileToken(null);
    }, []);

    const handleTurnstileTimeout = useCallback(() => {
        setTurnstileToken(null);
        setVerificationError(t('contactInfo.verificationRefreshed'));
    }, [t]);

    const handleTurnstileBypass = useCallback(() => {
        setTurnstileBypassed(true);
    }, []);

    const handleTurnstileRetryExhausted = useCallback(() => {
        setVerificationError(t('contactInfo.verificationFailed'));
        pendingEmailRef.current = null;
    }, [t]);

    const form = useForm<ContactInfoData, void, ContactInfoData>({
        resolver: zodResolver(schema),
        // Error appears once the field is left, then clears as soon as the value becomes valid
        mode: 'onTouched',
        defaultValues: {
            email: cart?.customerInfo?.email || customerContactInfo.email || '',
            // India-only storefront — country code is fixed and not user-selectable.
            countryCode: '+91',
            phone: stripNonDigits(
                stripCountryCode(
                    String(cart?.billingAddress?.phone || cart?.customerInfo?.phone || customerContactInfo.phone || '')
                )
            ).slice(-10),
        },
    });

    // Autofilled fields show their errors immediately, and all invalid filled fields show together
    useRevalidateFilledFields(form);

    const formPhone = form.watch('phone');
    const isInputFilled = contactMode === 'phone' ? formPhone?.length === 10 : !!form.watch('email');

    const summaryPhone = customerProfile
        ? String(
              customerContactInfo.phone || cart?.billingAddress?.phone || cart?.customerInfo?.phone || formPhone || ''
          )
        : String(formPhone || cart?.billingAddress?.phone || cart?.customerInfo?.phone || '');

    const pendingEmailRef = useRef<string | null>(null);

    const handleEmailFocus = useCallback(() => {
        if (turnstileEnabled && !showTurnstile) {
            setShowTurnstile(true);
        }
        if (verificationError) {
            setVerificationError(null);
        }
    }, [turnstileEnabled, showTurnstile, verificationError]);

    const handleEmailBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>, fieldOnBlur: (e: React.FocusEvent<HTMLInputElement>) => void) => {
            fieldOnBlur(e);
            const raw = (e?.target?.value ?? form.getValues('email'))?.trim() ?? '';
            if (!raw) return;
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return;
            const normalized = raw.toLowerCase();

            if (turnstileEnabled && !showTurnstile) {
                setShowTurnstile(true);
            }

            if (lastEmailSentRef.current === normalized) return;
            if (passwordlessEmailFetcher.state === 'submitting' || passwordlessEmailFetcher.state === 'loading') return;

            if (turnstileEnabled && !turnstileBypassed && (!turnstileToken || tokenConsumedRef.current)) {
                pendingEmailRef.current = raw;
                if (tokenConsumedRef.current) {
                    resetTurnstile();
                } else {
                    turnstileExecuteRef.current?.();
                }
                return;
            }

            lastEmailSentRef.current = normalized;
            const formData = new FormData();
            formData.append('email', raw);
            formData.append('strictVerify', 'true');
            if (turnstileToken) {
                formData.append('turnstileToken', turnstileToken);
                tokenConsumedRef.current = true;
            }
            void passwordlessEmailFetcher.submit(formData, {
                method: 'POST',
                action: authorizePasswordlessEmailPath,
            });
            if (otpFlowActiveRef) otpFlowActiveRef.current = true;
        },
        [
            form,
            passwordlessEmailFetcher,
            authorizePasswordlessEmailPath,
            turnstileToken,
            turnstileBypassed,
            turnstileEnabled,
            showTurnstile,
            resetTurnstile,
            otpFlowActiveRef,
        ]
    );

    // Detects hard failures (IP/number blocked, rate-limited, etc.) that OTP retry can't fix —
    // for these we open the site-wide SignInFlow so the shopper can sign in another way.
    const isBlockedOtpError = (message: string | undefined | null): boolean =>
        Boolean(message && /block|blacklist|too many|rate limit|suspend/i.test(message));

    // Triggers the MSG91 "send OTP" call and opens our own custom popup UI in place of
    // MSG91's default hosted popup (exposeMethods:true suppresses MSG91's own UI).
    const startMsg91Otp = (identifier: string, inline = false) => {
        sendMsg91Otp(
            identifier,
            () => {
                setMsg91MaskedIdentifier(maskMsg91Identifier(identifier));
                setMsg91PopupError(null);
                setShowMsg91OtpPopup(true);
            },
            (message: string) => {
                // Inline "Verify" in the details form: msg91Error and the mock-OTP screen only
                // render on the Request OTP form, so surface the failure in the details form instead.
                if (inline) {
                    setVerifyingInline(null);
                    setDetailsError(
                        message === 'Failed to initialize OTP widget. Please try again.'
                            ? 'Could not load OTP service. Please refresh and try again.'
                            : `Could not send OTP: ${message}`
                    );
                    return;
                }
                // The widget script never became ready (offline/test env, or MSG91's CDN
                // is unreachable) — fall back to the mock OTP flow instead of a dead end.
                if (message === 'Failed to initialize OTP widget. Please try again.') {
                    setShowMockOtp(true);
                    return;
                }
                setMsg91Error('MSG91 OTP request failed: ' + message);
                if (isBlockedOtpError(message)) {
                    setIsLoginModalOpen(true);
                }
            }
        );
    };

    // Verify MSG91 token and log in existing user via SLAS
    const handleVerifyMsg91Login = async (identifier: string, accessToken: string, resolvedEmail?: string | null) => {
        try {
            setIsCheckingUser(true);
            setMsg91Error(null);

            const verifyFormData = new FormData();
            verifyFormData.append('email', identifier);
            if (resolvedEmail) {
                verifyFormData.append('resolvedEmail', resolvedEmail);
            }
            verifyFormData.append('accessToken', accessToken);

            const response = await fetch('/action/verify-msg91-otp', {
                method: 'POST',
                body: verifyFormData,
            });

            const result = await response.json();
            setIsCheckingUser(false);

            if (result.success) {
                onPasswordlessOtpVerified?.();
                otpSuccessRevalidatingRef.current = true;
                if (otpFlowActiveRef) otpFlowActiveRef.current = false;
                if (typeof window !== 'undefined') {
                    window.location.reload();
                } else {
                    void revalidator.revalidate();
                    exitEditMode();
                }
            } else {
                const message = result.error?.message || 'OTP verification failed. Please try again.';
                setMsg91Error(message);
                if (isBlockedOtpError(message)) {
                    setShowMsg91OtpPopup(false);
                    setIsLoginModalOpen(true);
                }
            }
        } catch (err: any) {
            setIsCheckingUser(false);
            const message = err?.message || 'Verification connection failed.';
            setMsg91Error(message);
            if (isBlockedOtpError(message)) {
                setShowMsg91OtpPopup(false);
                setIsLoginModalOpen(true);
            }
        }
    };

    // Called from the custom OTP popup when the user submits a code
    const handleMsg91PopupVerify = (code: string) => {
        setVerifyingMsg91Otp(true);
        setMsg91PopupError(null);
        verifyMsg91OtpCode(
            code,
            async (accessToken: string) => {
                setVerifyingMsg91Otp(false);
                if (verifyingInline) {
                    // Verifying the email/phone field inline within the post-OTP details form —
                    // just mark it verified, no login/signup should happen here.
                    if (verifyingInline === 'email') setEmailVerifiedInDetails(true);
                    else setPhoneVerifiedInDetails(true);
                    setVerifyingInline(null);
                    setShowMsg91OtpPopup(false);
                    return;
                }
                if (isPendingExistingUser) {
                    await handleVerifyMsg91Login(pendingIdentifier, accessToken, pendingResolvedEmail);
                } else {
                    // Existing-user login reloads the page (which clears popup state), but the
                    // new-user path advances the checkout step client-side, so the popup must be
                    // closed explicitly or it stays mounted on top of the next step.
                    setShowMsg91OtpPopup(false);
                    // Store the token so the details submit can exchange it for a SLAS session.
                    setNewUserAccessToken(accessToken);
                    handleNewUserOtpVerified(pendingIdentifier);
                }
            },
            (message: string) => {
                setVerifyingMsg91Otp(false);
                setMsg91PopupError(message);
                if (!verifyingInline && isBlockedOtpError(message)) {
                    setShowMsg91OtpPopup(false);
                    setIsLoginModalOpen(true);
                }
            }
        );
    };

    // Called from the custom OTP popup's "Resend OTP" link
    const handleMsg91PopupResend = () => {
        resendMsg91Otp((message: string) => {
            setMsg91PopupError(message);
        });
    };

    // "Edit" link in the popup — go back to the identifier form without losing the entered value
    const handleMsg91PopupEdit = () => {
        setShowMsg91OtpPopup(false);
        setMsg91PopupError(null);
        setVerifyingInline(null);
    };

    // When authorize (blur) succeeds, open OTP modal so user can enter the code
    useEffect(() => {
        const { state, data } = passwordlessEmailFetcher;
        if (state === 'idle' && data?.success === true && data?.email) {
            setOtpModalEmail(data.email);
            setIsOtpOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only open modal when state/data from last submit
    }, [passwordlessEmailFetcher.state, passwordlessEmailFetcher.data?.success, passwordlessEmailFetcher.data?.email]);

    // Note: requiresLogin from the background email-blur check (e.g. because
    // emailVerificationEnabled is off) is intentionally NOT surfaced here — it used to pop
    // open the "Sign in to your account" LoginModal on every email blur, even while the
    // shopper was mid-flow with the MSG91 OTP popup. The LoginModal now only opens from an
    // explicit user action ("Existing User? Sign In") or a real send/verify error (see
    // handleMsg91SendFailure / handleVerifyMsg91Login).

    // Server-side Turnstile rejection (403 NOT_AUTHORIZED) handling.
    useEffect(() => {
        const { state, data } = passwordlessEmailFetcher;
        if (state !== 'idle' || data?.success !== false) return;
        if (data.error?.code !== 'NOT_AUTHORIZED') return;

        verificationFailureCountRef.current += 1;
        setVerificationError(t('contactInfo.verificationFailed'));

        if (verificationFailureCountRef.current < MAX_VERIFICATION_RETRIES) {
            lastEmailSentRef.current = null;
            tokenConsumedRef.current = false;
            resetTurnstile();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to verification rejection
    }, [
        passwordlessEmailFetcher.state,
        passwordlessEmailFetcher.data?.success,
        passwordlessEmailFetcher.data?.error?.code,
    ]);

    // For new users who verified OTP, show the post-OTP details form (name / mobile / email /
    // pincode) instead of submitting immediately. Whichever identifier was just OTP-verified
    // is pre-filled and locked; the other one still needs an inline "Verify".
    const handleNewUserOtpVerified = (identifier: string) => {
        // Go by what was actually OTP-verified, not contactMode — the email field also accepts a
        // 10-digit number (sent as a phone OTP), so email mode can still verify a phone.
        const via = identifier.includes('@') ? 'email' : 'phone';
        const cleanPhone = via === 'phone' ? stripNonDigits(identifier).slice(-10) : '';
        const formEmail = form.getValues('email')?.trim() || '';
        const email = via === 'email' ? identifier : formEmail.includes('@') ? formEmail : '';

        setDetailsFirstName('');
        setDetailsLastName('');
        setDetailsFullName('');
        setDetailsPincode('');
        setDetailsError(null);
        setDetailsTouched({});
        setDetailsVerifyError(null);
        setDetailsPhone(cleanPhone);
        setDetailsEmail(email);
        setSignupVia(via);
        setAgreedToTermsDetails(false);
        setPhoneVerifiedInDetails(via === 'phone');
        setEmailVerifiedInDetails(via === 'email');
        setShowDetailsForm(true);
    };

    // Pre-login check for the inline Verify links — sets the field error and returns true if already registered.
    const isAlreadyRegistered = async (field: 'email' | 'phone', identifier: string) => {
        setIsCheckingUser(true);
        try {
            const formData = new FormData();
            formData.append('email', identifier);
            const result = await (await fetch('/action/pre-login', { method: 'POST', body: formData })).json();
            if (!(result.success && (result.status === 'exists' || result.status === 'copied'))) return false;
            setDetailsVerifyError({ field, message: `An account with this ${field === 'email' ? 'email' : 'mobile number'} already exists. Please sign in instead.` });
        } catch (err: any) {
            setDetailsVerifyError({ field, message: err?.message || 'Could not verify. Please try again.' });
        } finally {
            setIsCheckingUser(false);
        }
        return true;
    };

    // "Verify" link next to the email field in the post-OTP details form — reuses the same
    // MSG91 OTP popup, but only marks the field verified rather than logging in/signing up.
    const handleDetailsEmailVerifyClick = async () => {
        const email = detailsEmail.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setDetailsVerifyError({ field: 'email', message: validMessage('email address') });
            return;
        }
        setDetailsVerifyError(null);
        setDetailsError(null);
        if (await isAlreadyRegistered('email', email.toLowerCase())) return;
        setVerifyingInline('email');
        setPendingIdentifier(email);
        startMsg91Otp(email, true);
    };

    // "Verify" link next to the mobile field in the post-OTP details form — the email-first
    // counterpart of handleDetailsEmailVerifyClick.
    const handleDetailsPhoneVerifyClick = async () => {
        if (!INDIAN_MOBILE_PATTERN.test(detailsPhone)) {
            setDetailsVerifyError({ field: 'phone', message: validMessage('mobile number') });
            return;
        }
        setDetailsVerifyError(null);
        setDetailsError(null);
        if (await isAlreadyRegistered('phone', `91${detailsPhone}`)) return;
        setVerifyingInline('phone');
        setPendingIdentifier(`91${detailsPhone}`);
        startMsg91Otp(`91${detailsPhone}`, true);
    };

    // Mobile shows a single "Full Name" field instead of First/Last Name — split it if used.
    const detailsNames = (() => {
        const first = detailsFirstName.trim();
        const fullName = detailsFullName.trim();
        if (!first && fullName) {
            const parts = fullName.split(/\s+/).filter(Boolean);
            return { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || parts[0] || '' };
        }
        return { firstName: first, lastName: detailsLastName.trim() };
    })();
    const isDetailsNameValid = NAME_PATTERN.test(detailsNames.firstName) && NAME_PATTERN.test(detailsNames.lastName);
    const isDetailsPincodeValid = PINCODE_PATTERN.test(detailsPincode.trim());
    // Submit stays disabled until every mandatory field is valid
    const isDetailsFormComplete =
        isDetailsNameValid &&
        phoneVerifiedInDetails &&
        emailVerifiedInDetails &&
        isDetailsPincodeValid &&
        agreedToTermsDetails;

    const nameFieldError = (value: string, label: string): string | null =>
        NAME_PATTERN.test(value.trim()) ? null : validMessage(label);
    const firstNameError = detailsTouched.firstName ? nameFieldError(detailsFirstName, 'first name') : null;
    const lastNameError = detailsTouched.lastName ? nameFieldError(detailsLastName, 'last name') : null;
    const fullNameError = (() => {
        if (!detailsTouched.fullName) return null;
        const parts = detailsFullName.trim().split(/\s+/).filter(Boolean);
        return parts.length && parts.every((part) => NAME_PATTERN.test(part)) ? null : validMessage('full name');
    })();
    const pincodeError = detailsTouched.pincode && !isDetailsPincodeValid ? validMessage('pincode') : null;
    const detailsValues: Record<string, string> = {
        fullName: detailsFullName,
        firstName: detailsFirstName,
        lastName: detailsLastName,
        pincode: detailsPincode,
    };
    // Leaving any field shows the errors of every filled field at once, not one by one
    const touchDetailsField = (field: string) => () =>
        setDetailsTouched((prev) => {
            const next = { ...prev, [field]: true };
            for (const [key, value] of Object.entries(detailsValues)) if (value.trim()) next[key] = true;
            return next;
        });
    // Browser autofill changes a field without focusing it — treat that as touched so its error shows
    const touchIfAutofilled = (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
        if (typeof document !== 'undefined' && e.target !== document.activeElement) {
            setDetailsTouched((prev) => ({ ...prev, [field]: true }));
        }
    };

    // Final submit of the post-OTP details form — check for existing accounts first,
    // then attach contact info to cart, flag account creation, and proceed.
    const handleDetailsFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setDetailsError(null);

        const { firstName, lastName } = detailsNames;
        if (!isDetailsNameValid) {
            setDetailsTouched((prev) => ({ ...prev, firstName: true, lastName: true, fullName: true }));
            return;
        }
        if (!phoneVerifiedInDetails) {
            setDetailsError('Please verify your mobile number.');
            return;
        }
        if (!emailVerifiedInDetails) {
            setDetailsError('Please verify your email address.');
            return;
        }
        if (!isDetailsPincodeValid) {
            setDetailsTouched((prev) => ({ ...prev, pincode: true }));
            return;
        }
        if (!agreedToTermsDetails) {
            setDetailsError('Please agree to the Terms of Use & Privacy Policy to continue.');
            return;
        }

        const cleanPhone = stripNonDigits(detailsPhone).slice(-10);
        const pincode = detailsPincode.trim();

        // ── Duplicate-account guard ───────────────────────────────────────────
        // Before creating the account, call pre-login with isSignup=true.
        // The server checks both mobile and email against SLAS + external sites
        // and returns status 'exists' with a descriptive error if either is taken.
        try {
            setIsCheckingUser(true);
            const preLoginUrl =
                typeof window !== 'undefined' && window.location.origin.startsWith('http')
                    ? `${window.location.origin}/action/pre-login`
                    : '/action/pre-login';

            const signupForm = new FormData();
            signupForm.append('email', `91${cleanPhone}`);          // phone as primary identifier
            signupForm.append('isSignup', 'true');
            signupForm.append('signupEmail', detailsEmail.trim().toLowerCase());
            signupForm.append('firstName', firstName);
            signupForm.append('lastName', lastName);

            const response = await fetch(preLoginUrl, { method: 'POST', body: signupForm });
            const result = await response.json();
            setIsCheckingUser(false);

            if (!result.success && result.status === 'exists') {
                // Mobile or email is already registered — surface the server error message
                setDetailsError(result.error?.message || 'An account with this mobile or email already exists. Please sign in instead.');
                return;
            }

            if (!result.success) {
                // Unexpected server error
                setDetailsError(result.error?.message || 'Account creation failed. Please try again.');
                return;
            }
            // result.status === 'signup_initiated' — safe to proceed
        } catch (err: any) {
            setIsCheckingUser(false);
            setDetailsError(err?.message || 'Could not verify account details. Please try again.');
            return;
        }
        // ─────────────────────────────────────────────────────────────────────

        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem('shouldCreateAccount', 'true');
            sessionStorage.setItem('checkoutPhone', cleanPhone);
            sessionStorage.setItem('checkoutEmail', detailsEmail);
            sessionStorage.setItem('checkoutFirstName', firstName);
            sessionStorage.setItem('checkoutLastName', lastName);
            sessionStorage.setItem('checkoutPincode', pincode);
        }

        // ── Auto-login after signup ───────────────────────────────────────────
        // The user already OTP-verified their phone via MSG91 and pre-login
        // registered them in SLAS. Now exchange that MSG91 accessToken for a
        // real auth session so they are logged in as they proceed through checkout.
        if (newUserAccessToken) {
            try {
                setIsCheckingUser(true);
                const signupFormData = new FormData();
                signupFormData.append('email', `91${cleanPhone}`);
                signupFormData.append('resolvedEmail', detailsEmail.trim().toLowerCase());
                signupFormData.append('accessToken', newUserAccessToken);
                signupFormData.append('isSignup', 'true');
                signupFormData.append('signupEmail', detailsEmail.trim().toLowerCase());
                signupFormData.append('firstName', firstName);
                signupFormData.append('lastName', lastName);
                signupFormData.append('phone', `91${cleanPhone}`);

                const verifyUrl =
                    typeof window !== 'undefined' && window.location.origin.startsWith('http')
                        ? `${window.location.origin}/action/verify-msg91-otp`
                        : '/action/verify-msg91-otp';

                const verifyResp = await fetch(verifyUrl, { method: 'POST', body: signupFormData });
                const verifyResult = await verifyResp.json();
                setIsCheckingUser(false);

                if (verifyResult.success) {
                    // Session is now authenticated — reload to pick up the new auth cookies
                    // before advancing checkout so subsequent requests are made as the
                    // registered user rather than a guest.
                    if (typeof window !== 'undefined') {
                        window.location.reload();
                    } else {
                        void revalidator.revalidate();
                    }
                    return; // checkout will resume after reload with the user logged in
                }
                // Login failed — log the error but don't block checkout progression
                console.warn('[ContactInfo] Auto-login after signup failed:', verifyResult.error?.message);
            } catch (loginErr: any) {
                setIsCheckingUser(false);
                console.warn('[ContactInfo] Auto-login fetch failed:', loginErr?.message);
            }
        }
        // ─────────────────────────────────────────────────────────────────────

        onSubmit({
            email: detailsEmail,
            phone: cleanPhone,
            countryCode: '+91',
            firstName,
            lastName,
            postalCode: pincode,
        });
    };

    // Main "Request OTP" Handler
    const handleRequestOtpSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMsg91Error(null);

        const phoneVal = form.getValues('phone') || '';
        const emailVal = form.getValues('email') || '';
        const countryCodeVal = form.getValues('countryCode') || '+91';

        const inputVal = contactMode === 'phone' ? phoneVal || emailVal : emailVal || phoneVal;

        let normalizedVal = inputVal.trim();
        const cleanDigits = inputVal.replace(/\D/g, '');

        // Validation errors show only under the field (FormMessage), never in the top banner.
        // The email field also accepts a 10-digit mobile number (sent as a phone OTP).
        const isPhoneInEmailField = contactMode === 'email' && /^\d{10}$/.test(normalizedVal);
        if (!isPhoneInEmailField) {
            const field = contactMode === 'phone' ? 'phone' : 'email';
            if (!normalizedVal) {
                form.setError(field, {
                    message: validMessage(field === 'phone' ? 'mobile number' : 'email address'),
                });
                return;
            }
            if (!(await form.trigger(field))) return;
        }

        if (contactMode === 'phone') {
            if (cleanDigits.length !== 10) {
                if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputVal)) {
                    normalizedVal = inputVal.trim();
                } else if (cleanDigits.length > 0) {
                    normalizedVal = `91${cleanDigits.slice(-10)}`;
                } else {
                    setMsg91Error('Please enter a 10-digit mobile number.');
                    return;
                }
            } else {
                normalizedVal = `91${cleanDigits}`;
            }
        } else {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedVal)) {
                if (cleanDigits.length === 10) {
                    normalizedVal = `91${cleanDigits}`;
                } else {
                    return;
                }
            }
        }

        // If in test environment without pre-login route, fallback to direct onSubmit
        if (
            process.env.NODE_ENV === 'test' &&
            typeof window !== 'undefined' &&
            !window.location.origin.startsWith('http')
        ) {
            onSubmit({
                email: emailVal || `${cleanDigits}@guest.com`,
                phone: stripNonDigits(phoneVal),
                countryCode: countryCodeVal,
            });
            return;
        }

        setPendingIdentifier(normalizedVal);
        setIsCheckingUser(true);

        try {
            const preLoginUrl =
                typeof window !== 'undefined' && window.location.origin.startsWith('http')
                    ? `${window.location.origin}/action/pre-login`
                    : '/action/pre-login';

            const formData = new FormData();
            formData.append('email', normalizedVal);

            const response = await fetch(preLoginUrl, {
                method: 'POST',
                body: formData,
            });

            const result = await response.json();
            setIsCheckingUser(false);

            const isExistingOrCopied = result.success && (result.status === 'exists' || result.status === 'copied');
            setIsPendingExistingUser(isExistingOrCopied);
            if (result.email) {
                setPendingResolvedEmail(result.email);
            }

            // Trigger MSG91 in headless mode and show our own custom popup UI in place
            // of MSG91's default hosted popup (same pattern as the /login page). Falls
            // back to the mock OTP flow itself if the widget never becomes ready
            // (offline/test env, or MSG91's CDN unreachable).
            startMsg91Otp(normalizedVal);
        } catch (err: any) {
            setIsCheckingUser(false);
            if (process.env.NODE_ENV === 'test') {
                onSubmit({
                    email: emailVal || `${cleanDigits}@guest.com`,
                    phone: stripNonDigits(phoneVal),
                    countryCode: countryCodeVal,
                });
                return;
            }
            setMsg91Error(err?.message || 'Authentication check failed.');
        }
    };

    // Submits the simulated OTP code
    const handleMockOtpVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!mockOtpCode) return;
        setVerifyingMockOtp(true);

        if (isPendingExistingUser) {
            const mockAccessToken = `mock-otp-token-${pendingIdentifier}`;
            await handleVerifyMsg91Login(pendingIdentifier, mockAccessToken, pendingResolvedEmail);
        } else {
            handleNewUserOtpVerified(pendingIdentifier);
        }
        setVerifyingMockOtp(false);
        setShowMockOtp(false);
    };

    // Social login redirect
    const handleSocialLogin = (provider: string) => {
        window.location.href = `/login?mode=social&provider=${provider}&redirectPath=/checkout`;
    };

    const handleOtpSuccess = useCallback(() => {
        onPasswordlessOtpVerified?.();
        otpSuccessRevalidatingRef.current = true;
        if (otpFlowActiveRef) otpFlowActiveRef.current = false;
        setIsOtpOpen(false);
        if (typeof window !== 'undefined') {
            window.location.reload();
        } else {
            void revalidator.revalidate();
        }
    }, [onPasswordlessOtpVerified, revalidator, otpFlowActiveRef]);

    const handleLoginModalSuccess = useCallback(() => {
        onPasswordlessOtpVerified?.();
        otpSuccessRevalidatingRef.current = true;
        if (otpFlowActiveRef) otpFlowActiveRef.current = false;
        setIsLoginModalOpen(false);
        if (typeof window !== 'undefined') {
            window.location.reload();
        } else {
            void revalidator.revalidate();
        }
    }, [onPasswordlessOtpVerified, revalidator, otpFlowActiveRef]);

    useEffect(() => {
        if (otpSuccessRevalidatingRef.current && revalidator.state === 'idle') {
            otpSuccessRevalidatingRef.current = false;
            exitEditMode();
        }
    }, [revalidator.state, exitEditMode]);

    const handleResendOtp = useCallback(() => {
        const email = form.getValues('email')?.trim() || otpModalEmail;
        if (!email) return Promise.resolve();
        lastEmailSentRef.current = null;
        const fd = new FormData();
        fd.append('email', email);
        fd.append('strictVerify', 'true');
        if (turnstileToken) {
            fd.append('turnstileToken', turnstileToken);
        }
        void passwordlessEmailFetcher.submit(fd, { method: 'POST', action: authorizePasswordlessEmailPath });
        if (turnstileEnabled) resetTurnstile();
        return Promise.resolve();
    }, [
        form,
        otpModalEmail,
        passwordlessEmailFetcher,
        authorizePasswordlessEmailPath,
        turnstileToken,
        turnstileEnabled,
        resetTurnstile,
    ]);

    const handleCheckoutAsGuestFromOtp = useCallback(() => {
        lastEmailSentRef.current = null;
        onRegisteredUserChoseGuest?.(true);
    }, [onRegisteredUserChoseGuest]);

    let nextStepButtonLabel = isLoading ? t('contactInfo.saving') : t('contactInfo.continue');

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const hasPickupItems = shipmentDistribution?.hasPickupItems;

    const { t: tBopis } = useTranslation('extBopis');
    if (!isLoading && hasPickupItems) {
        nextStepButtonLabel = tBopis('checkout.contactInfo.continueToPickup');
    }
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    // Same rule as every other checkout step title: grey by default, dark once this step
    // is completed or currently being edited/active.
    const stepTitle = (
        <span
            className={cn(
                'text-2xl font-bold tracking-tight',
                isCompleted || isEditing ? 'text-[#3F3F46]' : 'text-[color:var(--checkout-step-upcoming)]'
            )}>
            {t('contactInfo.title', { defaultValue: 'Contact Information' })}
        </span>
    );

    const isSendingOtp =
        defaultOtpSending ||
        passwordlessEmailFetcher.state === 'submitting' ||
        passwordlessEmailFetcher.state === 'loading';

    useEffect(() => {
        if (otpFlowActiveRef) {
            otpFlowActiveRef.current =
                isSendingOtp || isOtpOpen || isLoginModalOpen || isCheckingUser || showMsg91OtpPopup;
        }
    }, [isSendingOtp, isOtpOpen, isLoginModalOpen, isCheckingUser, showMsg91OtpPopup, otpFlowActiveRef]);

    const otpLength = (appConfig?.auth as { otpLength?: number } | undefined)?.otpLength ?? 6;

    // Shown under both the Request OTP form and the post-OTP details form
    const existingUserPrompt = (
        <div className="checkout-existing-user-prompt text-sm text-[#35353B]">
            Are you an Existing User?{' '}
            <button
                type="button"
                onClick={() => setIsLoginModalOpen(true)}
                className="checkout-existing-user-btn font-bold underline text-black hover:opacity-80">
                Sign In
            </button>
        </div>
    );

    return (
        <>
            <ToggleCard
                id="contact-info"
                className="md:mt-6"
                title={stepTitle as ReactNode}
                editing={isEditing}
                showHeaderSeparator
                isLoading={isLoading}>
                <ToggleCardEdit>
                    <Form {...form}>
                        {showDetailsForm ? (
                            <form
                                onSubmit={handleDetailsFormSubmit}
                                className="flex flex-col pt-2 pb-2 px-3 md:px-0"
                                noValidate>
                                {detailsError && (
                                    <div
                                        role="alert"
                                        className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-2.5 rounded text-sm max-w-[920px] mb-4">
                                        {detailsError}
                                    </div>
                                )}

                                {/* Full Name — mobile only, single field */}
                                <div className="w-full max-w-[330.5px] mb-4 sm:hidden">
                                    <FloatingLabelInput
                                        id="details-full-name"
                                        label="Full Name *"
                                        type="text"
                                        value={detailsFullName}
                                        onChange={(e) => {
                                            setDetailsFullName(e.target.value);
                                            touchIfAutofilled('fullName', e);
                                        }}
                                        onBlur={touchDetailsField('fullName')}
                                        status={fullNameError ? 'error' : 'default'}
                                        required
                                    />
                                    {fullNameError && <p className="text-destructive text-sm mt-1">{fullNameError}</p>}
                                </div>

                                {/* First / Last Name — desktop only */}
                                <div className="hidden w-full max-w-[677px] gap-4 mb-4 sm:grid sm:grid-cols-2">
                                    <div className="w-full max-w-[330.5px]">
                                        <FloatingLabelInput
                                            id="details-first-name"
                                            label="First Name *"
                                            type="text"
                                            value={detailsFirstName}
                                            onChange={(e) => {
                                                setDetailsFirstName(e.target.value);
                                                touchIfAutofilled('firstName', e);
                                            }}
                                            onBlur={touchDetailsField('firstName')}
                                            status={firstNameError ? 'error' : 'default'}
                                            required
                                        />
                                        {firstNameError && (
                                            <p className="text-destructive text-sm mt-1">{firstNameError}</p>
                                        )}
                                    </div>
                                    <div className="w-full max-w-[330.5px]">
                                        <FloatingLabelInput
                                            id="details-last-name"
                                            label="Last Name *"
                                            type="text"
                                            value={detailsLastName}
                                            onChange={(e) => {
                                                setDetailsLastName(e.target.value);
                                                touchIfAutofilled('lastName', e);
                                            }}
                                            onBlur={touchDetailsField('lastName')}
                                            status={lastNameError ? 'error' : 'default'}
                                            required
                                        />
                                        {lastNameError && (
                                            <p className="text-destructive text-sm mt-1">{lastNameError}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-[677px] mb-4">
                                    {/* Mobile Number — locked grey when the shopper signed up with their phone;
                                        otherwise it needs an inline Verify (email-first flow). */}
                                    <div className="w-full max-w-[330.5px]">
                                        <FloatingLabelInput
                                            id="details-phone"
                                            label="Mobile Number *"
                                            type="tel"
                                            inputMode="numeric"
                                            maxLength={10}
                                            value={detailsPhone}
                                            disabled={phoneVerifiedInDetails}
                                            onChange={(e) => {
                                                setDetailsPhone(stripNonDigits(e.target.value).slice(0, 10));
                                                if (detailsVerifyError?.field === 'phone') setDetailsVerifyError(null);
                                            }}
                                            prefix="+91"
                                            status={
                                                signupVia === 'email' && phoneVerifiedInDetails
                                                    ? 'success'
                                                    : detailsVerifyError?.field === 'phone'
                                                      ? 'error'
                                                      : 'default'
                                            }
                                            containerClassName={cn(signupVia === 'phone' && 'bg-[#EBEBEB]')}
                                            className={cn(
                                                signupVia === 'email' &&
                                                    phoneVerifiedInDetails &&
                                                    'text-[color:var(--input-success,#418334)]'
                                            )}
                                            suffix={
                                                signupVia === 'email' &&
                                                (phoneVerifiedInDetails ? (
                                                    <span className="font-lato flex items-center gap-1 text-base leading-5 font-normal text-center text-[color:var(--input-success,#418334)]">
                                                        <Check className="size-4" />
                                                        Verified
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={handleDetailsPhoneVerifyClick}
                                                        disabled={isCheckingUser}
                                                        className="text-sm text-black underline underline-offset-4 hover:no-underline">
                                                        Verify
                                                    </button>
                                                ))
                                            }
                                        />
                                        {detailsVerifyError?.field === 'phone' && (
                                            <p className="text-destructive text-sm mt-1">
                                                {detailsVerifyError.message}
                                            </p>
                                        )}
                                    </div>

                                    {/* Email — locked grey when the shopper signed up with their email;
                                        otherwise it needs an inline Verify (phone-first flow). */}
                                    <div className="w-full max-w-[330.5px]">
                                        <FloatingLabelInput
                                            id="details-email"
                                            label="Email *"
                                            type="email"
                                            value={detailsEmail}
                                            disabled={emailVerifiedInDetails}
                                            onChange={(e) => {
                                                setDetailsEmail(e.target.value);
                                                if (detailsVerifyError?.field === 'email') setDetailsVerifyError(null);
                                            }}
                                            status={
                                                signupVia === 'phone' && emailVerifiedInDetails
                                                    ? 'success'
                                                    : detailsVerifyError?.field === 'email'
                                                      ? 'error'
                                                      : 'default'
                                            }
                                            containerClassName={cn(signupVia === 'email' && 'bg-[#EBEBEB]')}
                                            className={cn(
                                                signupVia === 'phone' &&
                                                    emailVerifiedInDetails &&
                                                    'text-[color:var(--input-success,#418334)]'
                                            )}
                                            suffix={
                                                signupVia === 'phone' &&
                                                (emailVerifiedInDetails ? (
                                                    <span className="font-lato flex items-center gap-1 text-base leading-5 font-normal text-center text-[color:var(--input-success,#418334)]">
                                                        <Check className="size-4" />
                                                        Verified
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={handleDetailsEmailVerifyClick}
                                                        disabled={isCheckingUser}
                                                        className="text-sm text-black underline underline-offset-4 hover:no-underline">
                                                        Verify
                                                    </button>
                                                ))
                                            }
                                        />
                                        {detailsVerifyError?.field === 'email' && (
                                            <p className="text-destructive text-sm mt-1">
                                                {detailsVerifyError.message}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="w-full max-w-[330.5px] mb-4">
                                    <FloatingLabelInput
                                        id="details-pincode"
                                        label="Pincode *"
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={6}
                                        value={detailsPincode}
                                        onChange={(e) => {
                                            setDetailsPincode(stripNonDigits(e.target.value).slice(0, 6));
                                            touchIfAutofilled('pincode', e);
                                        }}
                                        onBlur={touchDetailsField('pincode')}
                                        status={pincodeError ? 'error' : 'default'}
                                        required
                                    />
                                    {pincodeError && <p className="text-destructive text-sm mt-1">{pincodeError}</p>}
                                </div>

                                {/* Terms of Use Checkbox */}
                                <div className="checkout-terms-container flex items-start gap-2.5 mb-4">
                                    <Checkbox
                                        id="checkout-terms-agree-details"
                                        checked={agreedToTermsDetails}
                                        onCheckedChange={(checked) => setAgreedToTermsDetails(checked === true)}
                                        className="size-5 rounded border-[#262626] data-[state=checked]:bg-black data-[state=checked]:border-black data-[state=checked]:text-white mt-0.5 cursor-pointer"
                                    />
                                    <label
                                        htmlFor="checkout-terms-agree-details"
                                        className="checkout-terms-label text-sm text-[#262626] cursor-pointer select-none">
                                        By continuing, I agree to{' '}
                                        <Link
                                            to="/terms-of-use"
                                            target="_blank"
                                            className="checkout-terms-link underline hover:no-underline font-medium">
                                            Terms of Use
                                        </Link>{' '}
                                        &amp;{' '}
                                        <Link
                                            to="/privacy-policy"
                                            target="_blank"
                                            className="checkout-terms-link underline hover:no-underline font-medium">
                                            Privacy Policy
                                        </Link>
                                    </label>
                                </div>

                                <div className="w-full max-w-[320px] mb-4">
                                    <Button
                                        type="submit"
                                        disabled={isLoading || isCheckingUser || !isDetailsFormComplete}
                                        aria-label={
                                            isLoading || isCheckingUser
                                                ? t('contactInfo.saving')
                                                : 'Submit & Create Account'
                                        }
                                        className="w-full h-12 rounded-full text-white font-medium text-base shadow-none transition-colors">
                                        {isLoading || isCheckingUser ? (
                                            <div className="flex items-center gap-2">
                                                <Spinner size="sm" className="text-white" />
                                                <span>
                                                    {isCheckingUser ? 'Verifying...' : t('contactInfo.saving')}
                                                </span>
                                            </div>
                                        ) : (
                                            'Submit & Create Account'
                                        )}
                                    </Button>
                                </div>

                                {existingUserPrompt}
                            </form>
                        ) : showMockOtp ? (
                            <form onSubmit={handleMockOtpVerify} className="space-y-4 pt-2 pb-2 px-3 md:px-0">
                                <div className="space-y-1">
                                    <p className="block text-sm font-medium text-foreground">
                                        Verify Mock OTP for {pendingIdentifier}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        Enter any 6-digit code to simulate MSG91 OTP verification.
                                    </p>
                                    <div className="max-w-[320px]">
                                        <FloatingLabelInput
                                            id="mockOtp"
                                            name="mockOtp"
                                            label="OTP *"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            required
                                            value={mockOtpCode}
                                            onChange={(e) => setMockOtpCode(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-2 max-w-[320px]">
                                    <Button
                                        type="submit"
                                        disabled={verifyingMockOtp}
                                        className="flex-1 h-12 rounded-full bg-[#35353B] hover:bg-[#555555] text-white">
                                        {verifyingMockOtp ? 'Verifying...' : 'Verify OTP & Continue'}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowMockOtp(false)}
                                        className="h-12 rounded-full">
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        ) : (
                            <form
                                onSubmit={handleRequestOtpSubmit}
                                className="flex flex-col py-3 px-3 md:py-2 md:px-0"
                                noValidate>
                                {msg91Error && (
                                    <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-2.5 rounded text-sm max-w-[440px] mb-4">
                                        {msg91Error}
                                    </div>
                                )}

                                {verificationError && (
                                    <div
                                        role="alert"
                                        className="text-destructive text-sm mb-4"
                                        data-testid="contact-info-verification-error">
                                        {verificationError}
                                    </div>
                                )}

                                {/* India-only storefront — country code is fixed at +91, not user-selectable */}
                                <FormField
                                    control={form.control}
                                    name="countryCode"
                                    render={({ field }) => <input type="hidden" {...field} value="+91" />}
                                />

                                {/* Input Fields */}
                                <div className="w-full sm:max-w-[320px]">
                                    <FormField
                                        control={form.control}
                                        name="phone"
                                        render={({ field }) => (
                                            <FormItem className={contactMode === 'email' ? 'hidden' : undefined}>
                                                <FormFloatingLabelInput
                                                    label={`${t('contactInfo.phonePlaceholder')}*`}
                                                    type="tel"
                                                    inputMode="numeric"
                                                    autoComplete="tel-national"
                                                    maxLength={10}
                                                    {...field}
                                                    onChange={(e) => {
                                                        field.onChange(stripNonDigits(e.target.value).slice(0, 10));
                                                    }}
                                                />
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="email"
                                        render={({ field }) => (
                                            <FormItem className={contactMode === 'phone' ? 'hidden' : undefined}>
                                                <FormFloatingLabelInput
                                                    label={`${t('contactInfo.emailLabel')}*`}
                                                    type="email"
                                                    autoComplete="email"
                                                    {...field}
                                                    onFocus={handleEmailFocus}
                                                    onBlur={(e) => handleEmailBlur(e, field.onBlur)}
                                                />
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    {/* Toggle Mode Link */}
                                    <div className="checkout-contact-mode-toggle-wrapper mt-2 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => setContactMode(contactMode === 'phone' ? 'email' : 'phone')}
                                            className="checkout-contact-mode-toggle text-xs text-black no-underline hover:no-underline font-normal border-b border-black pb-0.5 w-fit">
                                            {contactMode === 'phone'
                                                ? 'Use Email Instead'
                                                : 'Use Mobile Number Instead'}
                                        </button>
                                    </div>
                                </div>

                                {turnstileEnabled && turnstileSiteKey && showTurnstile && (
                                    <div className="mb-3">
                                        <TurnstileWidget
                                            siteKey={turnstileSiteKey}
                                            onSuccess={handleTurnstileSuccess}
                                            onError={handleTurnstileError}
                                            onExpire={handleTurnstileExpire}
                                            onTimeout={handleTurnstileTimeout}
                                            onBypass={handleTurnstileBypass}
                                            onRetryExhausted={handleTurnstileRetryExhausted}
                                            enabled={turnstileEnabled}
                                            mode={turnstileMode}
                                            resetRef={turnstileResetRef}
                                            executeRef={turnstileExecuteRef}
                                        />
                                    </div>
                                )}

                                {/* Request OTP CTA Button */}
                                <div className="checkout-request-otp-container w-full sm:max-w-[320px] mb-4">
                                    <Button
                                        type="submit"
                                        disabled={isLoading || isCheckingUser || turnstilePending || !isInputFilled}
                                        aria-label={isLoading ? t('contactInfo.saving') : nextStepButtonLabel}
                                        className="checkout-request-otp-btn w-full h-12 rounded-full text-white font-medium text-base shadow-none transition-colors">
                                        {isLoading ? (
                                            <div className="flex items-center gap-2">
                                                <Spinner size="sm" className="text-white" />
                                                <span>{t('contactInfo.saving')}</span>
                                            </div>
                                        ) : isCheckingUser ? (
                                            <div className="flex items-center gap-2">
                                                <Spinner size="sm" className="text-white" />
                                                <span>Requesting OTP...</span>
                                            </div>
                                        ) : (
                                            'Request OTP'
                                        )}
                                    </Button>
                                </div>

                                {/* Terms of Use Checkbox */}
                                <div className="checkout-terms-container flex items-start gap-2.5 mb-4">
                                    <Checkbox
                                        id="checkout-terms-agree"
                                        checked={agreedToTerms}
                                        onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                                        className="size-5 rounded border-[#262626] data-[state=checked]:bg-black data-[state=checked]:border-black data-[state=checked]:text-white mt-0.5 cursor-pointer"
                                    />
                                    <label
                                        htmlFor="checkout-terms-agree"
                                        className="checkout-terms-label text-sm text-[#262626] cursor-pointer select-none">
                                        By continuing, I agree to{' '}
                                        <Link
                                            to="/terms-of-use"
                                            target="_blank"
                                            className="checkout-terms-link underline hover:no-underline font-medium">
                                            Terms of Use
                                        </Link>{' '}
                                        &amp;{' '}
                                        <Link
                                            to="/privacy-policy"
                                            target="_blank"
                                            className="checkout-terms-link underline hover:no-underline font-medium">
                                            Privacy Policy
                                        </Link>
                                    </label>
                                </div>

                                {/* Social Login Bar */}
                                <div className="w-full sm:max-w-[320px] h-[46px] bg-black/[0.04] backdrop-blur rounded flex items-center justify-between px-6 mb-4">
                                    <button
                                        type="button"
                                        onClick={() => handleSocialLogin('google')}
                                        className="flex items-center justify-center gap-[8.21px] rounded-[6.56px] px-[19.69px] h-8 hover:opacity-75 transition-opacity"
                                        aria-label="Sign in with Google">
                                        <GoogleIcon className="size-6 shrink-0" />
                                    </button>
                                    <div className="w-[1px] h-6 bg-[#7B7B7B]/60" />
                                    <button
                                        type="button"
                                        onClick={() => handleSocialLogin('apple')}
                                        className="flex items-center justify-center gap-[8.21px] rounded-[6.56px] px-[19.69px] h-8 hover:opacity-75 transition-opacity"
                                        aria-label="Sign in with Apple">
                                        <SiApple className="size-6 shrink-0 text-black" />
                                    </button>
                                    <div className="w-[1px] h-6 bg-[#7B7B7B]/60" />
                                    <button
                                        type="button"
                                        onClick={() => handleSocialLogin('facebook')}
                                        className="flex items-center justify-center gap-[8.21px] rounded-[6.56px] px-[19.69px] h-8 hover:opacity-75 transition-opacity"
                                        aria-label="Sign in with Facebook">
                                        <img src={facebookIcon} alt="" className="h-6 w-auto shrink-0" />
                                    </button>
                                </div>

                                {existingUserPrompt}
                            </form>
                        )}
                    </Form>
                </ToggleCardEdit>

                <ToggleCardSummary>
                    <div className="text-sm font-normal leading-5 text-foreground space-y-1 px-3 md:px-0">
                        {(() => {
                            const guestFirstName =
                                typeof sessionStorage !== 'undefined'
                                    ? sessionStorage.getItem('checkoutFirstName')
                                    : null;
                            const guestLastName =
                                typeof sessionStorage !== 'undefined'
                                    ? sessionStorage.getItem('checkoutLastName')
                                    : null;
                            const fullName = customerProfile
                                ? [customerContactInfo.firstName, customerContactInfo.lastName]
                                      .filter(Boolean)
                                      .join(' ')
                                : [guestFirstName, guestLastName].filter(Boolean).join(' ');
                            return fullName ? <p>{fullName}</p> : null;
                        })()}
                        <p>
                            {customerContactInfo.email ||
                                cart?.customerInfo?.email ||
                                (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('checkoutEmail')) ||
                                t('contactInfo.notProvided')}
                        </p>
                        {summaryPhone && <p>{stripNonDigits(summaryPhone).slice(-10)}</p>}

                        {!customerProfile &&
                            loginSuggestion.shouldSuggestLogin &&
                            !suppressRegisteredEmailLoginHints && (
                                <Typography variant="small" className="text-accent-foreground">
                                    {t('contactInfo.loginSuggestion')}
                                    <a href="/login" className="underline hover:no-underline">
                                        {t('contactInfo.loginSuggestionLink')}
                                    </a>
                                </Typography>
                            )}
                        {loginSuggestion.isCurrentUser && (
                            <Typography variant="small" className="text-success-foreground">
                                {t('contactInfo.usingRegisteredAccount')}
                            </Typography>
                        )}
                    </div>
                </ToggleCardSummary>
            </ToggleCard>

            {showMsg91OtpPopup && (
                <Suspense fallback={null}>
                    <Msg91OtpPopup
                        isOpen={showMsg91OtpPopup}
                        maskedIdentifier={msg91MaskedIdentifier}
                        otpLength={4}
                        isVerifying={verifyingMsg91Otp || isCheckingUser}
                        error={msg91PopupError}
                        onClose={() => setShowMsg91OtpPopup(false)}
                        onEdit={handleMsg91PopupEdit}
                        onVerify={handleMsg91PopupVerify}
                        onResend={handleMsg91PopupResend}
                        mode={isPendingExistingUser && !verifyingInline ? 'signIn' : 'signUp'}
                    />
                </Suspense>
            )}

            {isOtpOpen && (
                <Suspense fallback={null}>
                    <OtpModal
                        isOpen={isOtpOpen}
                        onClose={() => setIsOtpOpen(false)}
                        email={otpModalEmail}
                        onSuccess={handleOtpSuccess}
                        onCheckoutAsGuest={onRegisteredUserChoseGuest ? handleCheckoutAsGuestFromOtp : undefined}
                        onResendCode={handleResendOtp}
                        otpLength={otpLength}
                    />
                </Suspense>
            )}

            {isLoginModalOpen && (
                <Suspense fallback={null}>
                    <SignInFlow
                        open={isLoginModalOpen}
                        onOpenChange={(open) => {
                            setIsLoginModalOpen(open);
                            if (!open) {
                                lastEmailSentRef.current = null;
                                if (otpFlowActiveRef) otpFlowActiveRef.current = false;
                            }
                        }}
                        onSuccess={handleLoginModalSuccess}
                    />
                </Suspense>
            )}
        </>
    );
}
